import React, { useState, useEffect, useCallback } from 'react';
import { api, fmt } from '../utils/api';
import useCollapsible from '../utils/useCollapsible';

const PACE_COLORS = {
  under: { bg: 'rgba(0,212,170,0.1)', border: 'rgba(0,212,170,0.3)', text: '#00d4aa' },
  even: { bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.3)', text: '#fbbf24' },
  over: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
};

const PACE_ICON = { under: '🟢', even: '🟡', over: '🔴' };
const PACE_WORD = { under: 'under pace', even: 'right on pace', over: 'over pace' };

const PERIOD_NOUN = { week: 'week', month: 'month', year: 'year' };

function PaceHeadline({ pace, period }) {
  if (!pace) return null;
  const c = PACE_COLORS[pace.paceState] || PACE_COLORS.even;
  const noun = PERIOD_NOUN[period];

  return (
    <div style={{ ...styles.paceBanner, background: c.bg, border: `1px solid ${c.border}` }}>
      <div style={styles.paceHeadlineRow}>
        <span style={{ fontSize: '1.1rem' }}>{PACE_ICON[pace.paceState]}</span>
        <span style={{ ...styles.paceHeadlineText, color: c.text }}>
          {pace.paceState === 'even'
            ? `Right on pace for the ${noun}`
            : `${fmt.currency(pace.paceAmount)} ${PACE_WORD[pace.paceState]} this ${noun}`}
        </span>
      </div>
      <div style={styles.paceSubtext}>
        {fmt.currency(pace.spent)} spent of {fmt.currency(pace.budget)} budgeted &middot; {pace.timeElapsedPct.toFixed(0)}% of the {noun} elapsed, {pace.pctSpent.toFixed(0)}% of budget spent
      </div>
    </div>
  );
}

// Turns the AI's plain-text reply into readable blocks: consecutive "- "
// lines become an actual bulleted list (with a colored marker matching the
// pace state), everything else becomes a paragraph. No markdown library
// needed - the prompt already constrains the model to dashes-for-bullets,
// blank-line-separated paragraphs.
function RecapBody({ text, paceState }) {
  const c = PACE_COLORS[paceState] || PACE_COLORS.even;
  const blocks = text.trim().split(/\n\s*\n/); // paragraphs, separated by blank lines

  return (
    <div style={styles.body}>
      {blocks.map((block, i) => {
        const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
        const isList = lines.length > 0 && lines.every((l) => l.startsWith('- '));

        if (isList) {
          return (
            <ul key={i} style={styles.list}>
              {lines.map((l, j) => (
                <li key={j} style={styles.listItem}>
                  <span style={{ ...styles.listBullet, color: c.text }}>●</span>
                  <span>{l.replace(/^-\s*/, '')}</span>
                </li>
              ))}
            </ul>
          );
        }

        return <p key={i} style={styles.paragraph}>{lines.join(' ')}</p>;
      })}
    </div>
  );
}

export default function SpendingRecapCard() {
  const [collapsed, toggleCollapsed] = useCollapsible('spendingRecap');
  const [status, setStatus] = useState(null);
  const [period, setPeriod] = useState('month');
  const [recap, setRecap] = useState(null);
  const [pace, setPace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadedFor, setLoadedFor] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.getAIStatus();
      setStatus(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const hasAnyKey = status?.claudeConfigured || status?.openaiConfigured;
  const provider = status?.preferredProvider === 'openai' && status?.openaiConfigured
    ? 'openai'
    : (status?.claudeConfigured ? 'claude' : 'openai');

  const generate = useCallback(async (p) => {
    if (!hasAnyKey) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getAIRecap(p, provider);
      setRecap(data.reply);
      setPace(data.pace || null);
      setLoadedFor(p);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnyKey, provider]);

  const switchPeriod = (p) => {
    setPeriod(p);
    // No auto-generate on period switch anymore - the person clicks the
    // button themselves once they've picked the period they want.
  };

  const hasRecapForPeriod = recap && loadedFor === period;

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <button style={styles.collapseBtn} onClick={toggleCollapsed}>
          <span style={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
          <h2 style={styles.title}>🔔 Recap & Recommendations</h2>
        </button>
        {!collapsed && hasAnyKey && hasRecapForPeriod && (
          <button
            style={styles.refreshBtn}
            onClick={() => generate(period)}
            disabled={loading}
            title="Regenerate"
          >
            ↻
          </button>
        )}
      </div>

      {!collapsed && (!status ? (
        <p style={styles.empty}>Loading...</p>
      ) : !hasAnyKey ? (
        <p style={styles.empty}>
          Add a Claude or ChatGPT API key in AI settings to get a plain-language recap of your week or month, with
          specific spots to cut back or room to splurge.
        </p>
      ) : (
        <>
          <p style={styles.instruction}>Pick a period, then generate your recap:</p>
          <div style={styles.pillGroup}>
            <button
              style={{ ...styles.pill, ...(period === 'week' ? styles.pillActive : {}) }}
              onClick={() => switchPeriod('week')}
            >
              Week
            </button>
            <button
              style={{ ...styles.pill, ...(period === 'month' ? styles.pillActive : {}) }}
              onClick={() => switchPeriod('month')}
            >
              Month
            </button>
            <button
              style={{ ...styles.pill, ...(period === 'year' ? styles.pillActive : {}) }}
              onClick={() => switchPeriod('year')}
            >
              Year
            </button>
          </div>

          {error && <p style={styles.errorText}>{error}</p>}

          {loading ? (
            <p style={styles.empty}>Thinking it through...</p>
          ) : hasRecapForPeriod ? (
            <>
              <PaceHeadline pace={pace} period={period} />
              <RecapBody text={recap} paceState={pace?.paceState} />
            </>
          ) : (
            <button style={styles.generateBtn} onClick={() => generate(period)}>
              🔮 Click here for a Recap using AI
            </button>
          )}
        </>
      ))}
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.9rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  title: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  collapseBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  chevron: {
    color: '#6b7280',
    fontSize: '0.8rem',
    width: '0.8rem',
  },
  pillGroup: { display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.9rem' },
  instruction: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    margin: '0 0 0.6rem 0',
  },
  generateBtn: {
    display: 'block',
    width: '100%',
    background: 'rgba(0,212,170,0.1)',
    border: '1px solid rgba(0,212,170,0.4)',
    color: '#00d4aa',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
    fontSize: '0.88rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    cursor: 'pointer',
    textAlign: 'center',
  },
  pill: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '999px',
    padding: '5px 14px',
    fontSize: '0.72rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  pillActive: {
    background: '#00d4aa',
    color: '#04120e',
    border: '1px solid #00d4aa',
  },
  refreshBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '999px',
    width: '26px',
    height: '26px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    lineHeight: 1,
  },
  empty: { color: '#6b7280', fontSize: '0.85rem', lineHeight: 1.5, margin: 0 },
  errorText: { color: '#ef4444', fontSize: '0.85rem', margin: 0 },
  paceBanner: {
    borderRadius: '12px',
    padding: '0.85rem 1rem',
    marginBottom: '1.1rem',
  },
  paceHeadlineRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  paceHeadlineText: {
    fontSize: '1rem',
    fontWeight: '800',
    fontFamily: "'Syne', sans-serif",
  },
  paceSubtext: {
    color: '#9ca3af',
    fontSize: '0.76rem',
    marginTop: '0.3rem',
  },
  body: {},
  paragraph: {
    color: '#d1d5db',
    fontSize: '0.88rem',
    lineHeight: 1.65,
    margin: '0 0 0.9rem 0',
  },
  list: {
    listStyle: 'none',
    margin: '0 0 0.9rem 0',
    padding: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.55rem',
    color: '#d1d5db',
    fontSize: '0.88rem',
    lineHeight: 1.55,
    padding: '0.3rem 0',
  },
  listBullet: {
    fontSize: '0.5rem',
    marginTop: '0.4rem',
    flexShrink: 0,
  },
};
